import { Locales } from "adapters/i18n";
import type { RootState } from "redux/reducers";
import type { Fazenda } from "./types";

export const STORAGE_KEYS = {
  EMAIL: "Email",
  FARM_STATE: "fazenda",
  HELP_CENTER_PERMISSIONS: "centralDeAjuda",
  INTERNAL_USER: "UsuarioInterno",
  NEWS_REGISTERS: "@prodap-news-registers",
  SELECTED_CLIENT: "clienteSelecionado",
  SELECTED_FARM: "fazendaSelecionada",
  SELECTED_CONFIGURATION_TAB: "abaSelecionada",
  USER_ID: "IdUsuario",
  USER_LANGUAGE: "i18n-language",
  USER_MANAGEMENT_FORM_STATE: "userManagementFormState",
  USER_LOGIN: "Login",
};

type HelpCenterPermissions = {
  canAccess: boolean;
  supportAvailable: boolean;
};

/**
 * Retrieves Help Center permissions from session storage.
 */
export function getHelpCenterPermissionsFromStorage(): HelpCenterPermissions {
  try {
    const value = sessionStorage.getItem(STORAGE_KEYS.HELP_CENTER_PERMISSIONS);
    const parsedValue =
      value != null
        ? (JSON.parse(value) as HelpCenterPermissions)
        : { canAccess: false, supportAvailable: false };
    return {
      canAccess: parsedValue.canAccess,
      supportAvailable: parsedValue.supportAvailable,
    };
  } catch (error) {
    return {
      canAccess: false,
      supportAvailable: false,
    };
  }
}

/**
 * Stores help center permissions in sessionStorage.
 */
export function setHelpCenterPermissionsInStorage({
  canAccess,
  supportAvailable,
}: HelpCenterPermissions) {
  sessionStorage.setItem(
    STORAGE_KEYS.HELP_CENTER_PERMISSIONS,
    JSON.stringify({ canAccess, supportAvailable })
  );
}

/**
 * Retrieves the user's selected language from local storage.
 */
export function getI18nLanguageStorage(): Locales | null {
  const value = localStorage.getItem(STORAGE_KEYS.USER_LANGUAGE);
  return value != null ? (value as Locales) : null;
}

/**
 * Sets the user's preferred language in local storage.
 */
export function setI18nLanguageStorage(language: Locales) {
  localStorage.setItem(STORAGE_KEYS.USER_LANGUAGE, language);
}

/**
 * Retrieves the user ID from local storage.
 */
export function getUserIdFromStorage(): number | null {
  try {
    const value = localStorage.getItem(STORAGE_KEYS.USER_ID);

    if (value == null) {
      return null;
    }

    const parsedValue = Number(value);
    return Number.isNaN(parsedValue) ? null : parsedValue;
  } catch (error) {
    return null;
  }
}

/**
 * Retrieves the user login from local storage.
 */
export function getUserLoginFromStorage(): string | null {
  try {
    const value = localStorage.getItem(STORAGE_KEYS.USER_LOGIN);
    return value != null ? value : null;
  } catch (error) {
    return null;
  }
}

/**
 * Retrieves the internal user flag from local storage.
 */
export function getIsInternalUserFromStorage(): boolean {
  try {
    const value = localStorage.getItem(STORAGE_KEYS.INTERNAL_USER);
    return value != null ? JSON.parse(value) === true : false;
  } catch (error) {
    return false;
  }
}

/**
 * Retrieves the selected client ID from session storage.
 */
export function getSelectedClientFromStorage(): number | null {
  try {
    const selected = sessionStorage.getItem(STORAGE_KEYS.SELECTED_CLIENT);
    if (selected == null) {
      return null;
    }
    const parsed = Number(selected);
    return Number.isNaN(parsed) ? null : parsed;
  } catch (error) {
    return null;
  }
}

/**
 * Stores the selected client ID in session storage.
 */
export function setSelectedClientInStorage(clientId: number) {
  sessionStorage.setItem(
    STORAGE_KEYS.SELECTED_CLIENT,
    JSON.stringify(clientId)
  );
}

/**
 * Retrieves the user management form state from session storage and parses it as the specified type.
 */
export function getUserManagementFormStateFromStorage<
  T extends Record<string, unknown>
>(): T | null {
  try {
    const value = sessionStorage.getItem(
      STORAGE_KEYS.USER_MANAGEMENT_FORM_STATE
    );
    return value != null ? (JSON.parse(value) as T) : null;
  } catch (error) {
    return null;
  }
}

/**
 * Stores the user management form state in the session storage.
 */
export function setUserManagementFormStateInStorage(
  state: Record<string, unknown>
) {
  sessionStorage.setItem(
    STORAGE_KEYS.USER_MANAGEMENT_FORM_STATE,
    JSON.stringify(state)
  );
}

/**
 * Removes the user management form state from session storage.
 */
export function clearUserManagementFormStateInStorage() {
  sessionStorage.removeItem(STORAGE_KEYS.USER_MANAGEMENT_FORM_STATE);
}

/**
 * Retrieves the selected farm ID from session storage.
 */
export function getSelectedFarmIdFromStorage(): number | null {
  try {
    const selected = sessionStorage.getItem(STORAGE_KEYS.SELECTED_FARM);

    if (selected == null) {
      return null;
    }

    const parsed = Number(selected);
    return Number.isNaN(parsed) ? null : parsed;
  } catch (error) {
    return null;
  }
}

/**
 * Stores the selected farm ID in session storage.
 */
export function setSelectedFarmInStorage(farmId: number) {
  sessionStorage.setItem(STORAGE_KEYS.SELECTED_FARM, JSON.stringify(farmId));
}

/**
 *  Retrieves the farm state from session storage and parses it as the specified type.
 */
export function getFarmStateFromStorage<
  T extends Partial<RootState["fazenda"]>
>(): T | null {
  try {
    const value = sessionStorage.getItem(STORAGE_KEYS.FARM_STATE);
    return value != null ? (JSON.parse(value) as T) : null;
  } catch (error) {
    return null;
  }
}

/**
 * Retrieves the currently selected farm object (`Fazenda`) from farm state storage.
 */
export function getSelectedFarmStateFromStorage(): Fazenda | null {
  try {
    const farmState = getFarmStateFromStorage();
    const selectedFarmId = getSelectedFarmIdFromStorage();

    if (farmState == null || selectedFarmId == null) {
      return null;
    }

    if (!Array.isArray(farmState.Fazendas)) {
      return null;
    }

    const selectedFarm = farmState.Fazendas.find(
      (farm) => farm.IdFazenda === selectedFarmId
    );

    return selectedFarm ?? null;
  } catch (error) {
    return null;
  }
}

/**
 * Stores the farm state in session storage.
 */
export function setFarmStateInStorage(state: Partial<RootState["fazenda"]>) {
  sessionStorage.setItem(STORAGE_KEYS.FARM_STATE, JSON.stringify(state));
}

/**
 * Retrieves the selected configuration tab (used in `/cadastros` page) from session storage.
 */
export function getSelectedConfigurationTabFromStorage(): string | null {
  try {
    const value = sessionStorage.getItem(
      STORAGE_KEYS.SELECTED_CONFIGURATION_TAB
    );
    return value ?? null;
  } catch (error) {
    return null;
  }
}

/**
 * Stores the selected configuration tab (used in `/cadastros` page) in session storage.
 */
export function setSelectedConfigurationTabInStorage(tab: string) {
  sessionStorage.setItem(STORAGE_KEYS.SELECTED_CONFIGURATION_TAB, tab);
}

/**
 * Retrieves the user's email address from local storage.
 *
 * @returns The email address as a string if it exists in local storage,
 * or `null` if not found or if an error occurs.
 */
export function getEmailFromStorage(): string | null {
  try {
    const value = localStorage.getItem(STORAGE_KEYS.EMAIL);
    return value ?? null;
  } catch (error) {
    return null;
  }
}

/**
 * Stores the provided email address in the browser's local storage under the EMAIL key.
 *
 * @param email - The email address to be saved in local storage.
 */
export function setEmailInStorage(email: string) {
  localStorage.setItem(STORAGE_KEYS.EMAIL, email);
}

type NewsRegister = {
  [userLogin: string]: {
    [newsId: string]: {
      lastView: string;
      countViews: number;
    };
  };
};

export function getNewsRegistersFromStorage(): NewsRegister | null;
export function getNewsRegistersFromStorage(
  userLogin: string
): NewsRegister[string] | null;
export function getNewsRegistersFromStorage(
  userLogin?: string
): NewsRegister | NewsRegister[string] | null {
  try {
    const value = localStorage.getItem(STORAGE_KEYS.NEWS_REGISTERS);
    if (value == null) {
      return null;
    }

    const parsed = JSON.parse(value) as NewsRegister;

    if (userLogin != null) {
      return parsed[userLogin] ?? null;
    }

    return parsed;
  } catch (error) {
    return null;
  }
}

export function setNewsRegistersInStorage(
  newsRegisterForUser: NewsRegister[string][string],
  newsId: string,
  userLogin: string
): void;
export function setNewsRegistersInStorage(newsRegisters: NewsRegister): void;
export function setNewsRegistersInStorage(
  newsRegisters: NewsRegister | NewsRegister[string][string],
  newsId?: string,
  userLogin?: string
): void {
  if (userLogin == null || newsId == null) {
    localStorage.setItem(
      STORAGE_KEYS.NEWS_REGISTERS,
      JSON.stringify(newsRegisters)
    );
    return;
  }

  const currentRegisters = getNewsRegistersFromStorage() ?? {};
  const updatedRegisters = {
    ...currentRegisters,
    [userLogin]: {
      ...(currentRegisters[userLogin] ?? {}),
      [newsId]: { ...newsRegisters },
    },
  };

  localStorage.setItem(
    STORAGE_KEYS.NEWS_REGISTERS,
    JSON.stringify(updatedRegisters)
  );
}
